// src/utils/cachedFetch.js
// sessionStorage 기반 짧은 캐시 (stale-while-revalidate 비슷한 동작).
// - 같은 URL 을 즉시 가져와서 화면을 빠르게 그리고
// - 백그라운드에서 다시 받아 최신화 (콜백)

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5분

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (typeof ts !== "number") return null;
    return { ts, data };
  } catch {
    return null;
  }
}

function writeCache(key, data) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // quota 등 무시
  }
}

/**
 * 캐시 우선 fetch.
 * @param {string} url
 * @param {object} opts
 * @param {number} opts.ttl  - 캐시 TTL(ms). 기본 5분.
 * @param {boolean} opts.revalidate - true면 캐시 적중해도 백그라운드 재요청.
 * @returns {Promise<any>} 즉시 캐시(있으면) 또는 신규 응답
 */
export async function cachedFetch(url, { ttl = DEFAULT_TTL_MS, revalidate = true } = {}) {
  const key = `ff_cache::${url}`;
  const cached = readCache(key);
  const isFresh = cached && (Date.now() - cached.ts) < ttl;

  if (isFresh) {
    // 캐시 신선: 즉시 반환 + 필요시 백그라운드 갱신
    if (revalidate) {
      fetch(url).then(async (r) => {
        if (!r.ok) return;
        try {
          const data = await r.json();
          writeCache(key, data);
        } catch {}
      }).catch(() => {});
    }
    return cached.data;
  }

  // 캐시 없음 또는 만료: 정상 fetch
  const res = await fetch(url);
  if (!res.ok) {
    if (cached) return cached.data;     // 실패 시 만료 캐시라도 반환
    throw new Error(`HTTP ${res.status}`);
  }
  const data = await res.json();
  writeCache(key, data);
  return data;
}

/**
 * 캐시 강제 무효화 (특정 URL 또는 prefix 매칭).
 */
export function invalidateCache(urlOrPrefix) {
  const fullKey = `ff_cache::${urlOrPrefix}`;
  try {
    // 정확 일치 먼저
    sessionStorage.removeItem(fullKey);
    // prefix 매칭
    const prefix = fullKey;
    const toRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(prefix)) toRemove.push(k);
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {}
}
